import { useState, useEffect, useCallback, useRef } from "react";
import API_BASE_URL from "../config";

const DEFAULT_POLL_MS =
  Number((window._env_ && window._env_.REACT_APP_JOBS_POLL_MS) ||
    process.env.REACT_APP_JOBS_POLL_MS) || 2500;

/**
 * Polls the background-jobs list so the sidebar/activity menu reflect status in
 * near-real-time. Polling pauses while the browser tab is hidden and resumes on focus.
 * All background agent jobs (approval/matching/compliance/schema/takeoff) live in the
 * unified /api/v1/jobs feed — no supplementary fetch needed.
 * `startJob(endpoint, body)` POSTs to a trigger endpoint and returns the response.
 */
export default function useJobs({ pollMs = DEFAULT_POLL_MS, enabled = true } = {}) {
  const [jobs, setJobs] = useState([]);
  const [available, setAvailable] = useState(true);
  const timer = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/jobs`);
      if (res.status === 503) { setAvailable(false); return; }
      if (!res.ok) return;
      const data = await res.json();
      setJobs(Array.isArray(data) ? data : []);
      setAvailable(true);
    } catch (err) {
      // transient — keep last known list, but don't hide it from diagnostics
      // eslint-disable-next-line no-console
      console.error("Failed to poll jobs:", err);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let stopped = false;
    const tick = async () => {
      if (document.hidden) return;
      await refresh();
    };
    refresh();
    timer.current = setInterval(tick, pollMs);
    const onVis = () => { if (!document.hidden) refresh(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      stopped = true;
      if (timer.current) clearInterval(timer.current);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
      void stopped;
    };
  }, [enabled, pollMs, refresh]);

  const startJob = useCallback(async (endpoint, body) => {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error((await res.text()) || res.statusText);
    const data = await res.json();
    refresh(); // surface the new running job immediately
    return data;
  }, [refresh]);

  return { jobs, available, startJob, refresh };
}
